import { memo, useState, useCallback, useMemo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import MediaLightbox from '../MediaLightbox';
import GenerationStrip from './GenerationStrip';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Chip,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Delete, Blender, Layers } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { HANDLES, type BlendNodeData, type GenerateNodeData, type GenerationEntry, useCanvasStore } from '../useCanvasStore';
import { studioService } from '../../../../services/studio.service';
import { useStudioStore } from '../../stores/studio.store';
import { getAssetSrc } from '../../utils/assetUrl';
import { useNodeTextField } from './useNodeTextField';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';

function BlendNode({ id, data }: NodeProps & { data: BlendNodeData }) {
  const updateNodeData = useCanvasStore((s) => s.updateNodeData);
  const removeNode = useCanvasStore((s) => s.removeNode);
  const getConnectedInputs = useCanvasStore((s) => s.getConnectedInputs);
  const edges = useCanvasStore((s) => s.edges);
  const nodes = useCanvasStore((s) => s.nodes);
  const addAsset = useStudioStore((s) => s.addAsset);
  const projectId = useStudioStore((s) => s.projectId);
  const { enqueueSnackbar } = useSnackbar();
  const [lightbox, setLightbox] = useState(false);
  const syncInstruction = useMemo(() => (v: string) => updateNodeData(id, { instruction: v }), [id, updateNodeData]);
  const instructionField = useNodeTextField(data.instruction, syncInstruction);

  const mode = data.blendMode || 'ai';
  const overlayScale = data.overlayScale ?? 0.15;

  const blendInputs = edges
    .filter((e) => e.target === id && e.targetHandle === HANDLES.blendIn)
    .map((e) => ({ handle: e.targetHandle || '', sourceNode: nodes.find((n) => n.id === e.source)! }))
    .filter((x) => x.sourceNode);

  const handleSelectGeneration = useCallback((idx: number) => {
    const gens = data.generations || [];
    const gen = gens[idx];
    if (!gen) return;
    updateNodeData(id, {
      selectedGeneration: idx,
      resultImage: gen.image,
      resultMimeType: gen.mimeType,
      assetId: gen.assetId,
      gcsPath: gen.gcsPath,
      ...(gen.prompt !== undefined && { instruction: gen.prompt }),
    });
  }, [id, data.generations, updateNodeData]);

  const handleBlend = useCallback(async () => {
    const currentInputs = getConnectedInputs(id).filter((i) => i.handle === HANDLES.blendIn);
    if (!data.instruction.trim() || currentInputs.length < 1) return;
    updateNodeData(id, { isGenerating: true });

    try {
      const files: File[] = [];
      for (const input of currentInputs) {
        const img = (input.sourceNode.data as GenerateNodeData).resultImage;
        if (img) {
          const resp = await fetch(getAssetSrc(img, 'image/png'));
          const blob = await resp.blob();
          files.push(new File([blob], `blend-${files.length}.png`, { type: 'image/png' }));
        }
      }

      if (files.length < 1) {
        enqueueSnackbar('Connect at least 1 image', { variant: 'warning' });
        updateNodeData(id, { isGenerating: false });
        return;
      }

      const res = await studioService.blend(files, data.instruction, projectId || undefined);
      if (res.image) {
        const newGen: GenerationEntry = {
          image: res.image,
          mimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          prompt: data.instruction,
          timestamp: Date.now(),
        };
        const prevGens = data.generations || [];
        const newGens = [...prevGens, newGen];
        updateNodeData(id, {
          resultImage: res.image,
          resultMimeType: res.mimeType,
          assetId: res.assetId,
          gcsPath: res.gcsPath,
          isGenerating: false,
          generations: newGens,
          selectedGeneration: newGens.length - 1,
        });
        addAsset({
          type: 'image',
          data: res.image,
          mimeType: res.mimeType,
          prompt: data.instruction,
          tool: 'blend',
          serverId: res.assetId,
        });
      }
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data.instruction, projectId, updateNodeData, getConnectedInputs, addAsset, enqueueSnackbar]);

  const handleOverlay = useCallback(async () => {
    const currentInputs = getConnectedInputs(id).filter((i) => i.handle === HANDLES.blendIn);
    if (currentInputs.length < 2) {
      enqueueSnackbar('Connect 2 images: base (1st) + overlay (2nd)', { variant: 'warning' });
      return;
    }
    // Get GCS paths from connected nodes
    const baseData = currentInputs[0].sourceNode.data as GenerateNodeData;
    const overlayData = currentInputs[1].sourceNode.data as GenerateNodeData;

    if (!baseData.gcsPath || !overlayData.gcsPath) {
      enqueueSnackbar('Both images must have been generated/uploaded (need GCS paths)', { variant: 'warning' });
      return;
    }

    updateNodeData(id, { isGenerating: true });
    try {
      const result = await studioService.compositeImages(
        baseData.gcsPath,
        overlayData.gcsPath,
        {
          x: data.overlayX || 'center',
          y: data.overlayY || 'center',
          scale: overlayScale,
          opacity: data.overlayOpacity ?? 1,
        },
        projectId || undefined,
      );

      const newGen: GenerationEntry = {
        image: result.signedUrl,
        mimeType: 'image/png',
        gcsPath: result.gcsPath,
        prompt: `Overlay (${Math.round(overlayScale * 100)}%)`,
        timestamp: Date.now(),
      };
      const prevGens = data.generations || [];
      const newGens = [...prevGens, newGen];
      updateNodeData(id, {
        resultImage: result.signedUrl,
        resultMimeType: 'image/png',
        gcsPath: result.gcsPath,
        isGenerating: false,
        generations: newGens,
        selectedGeneration: newGens.length - 1,
      });
      enqueueSnackbar('Overlay composited!', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
      updateNodeData(id, { isGenerating: false });
    }
  }, [id, data, overlayScale, projectId, getConnectedInputs, updateNodeData, enqueueSnackbar]);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        width: 280,
        overflow: 'hidden',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id={HANDLES.blendIn}
        style={{ background: '#ff9800', width: 10, height: 10 }}
        title="Image Input"
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: '#e65100',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {mode === 'overlay' ? <Layers sx={{ fontSize: 16 }} /> : <Blender sx={{ fontSize: 16 }} />}
          <Typography variant="caption" fontWeight={600}>
            {mode === 'overlay' ? 'Image Overlay' : 'Blend Images'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => removeNode(id)} sx={{ color: 'inherit' }}>
          <Delete sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Mode toggle */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && updateNodeData(id, { blendMode: v })}
          size="small"
          fullWidth
          className="nodrag"
        >
          <ToggleButton value="ai" sx={{ fontSize: '0.7rem', py: 0.3 }}>
            <Blender sx={{ fontSize: 14, mr: 0.5 }} /> AI Blend
          </ToggleButton>
          <ToggleButton value="overlay" sx={{ fontSize: '0.7rem', py: 0.3 }}>
            <Layers sx={{ fontSize: 14, mr: 0.5 }} /> Overlay
          </ToggleButton>
        </ToggleButtonGroup>

        <Chip
          size="small"
          label={`${blendInputs.length} image(s) connected`}
          color={blendInputs.length >= (mode === 'overlay' ? 2 : 1) ? 'success' : 'default'}
          sx={{ fontSize: '0.7rem' }}
        />

        {mode === 'ai' ? (
          <>
            <TextField
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              placeholder="e.g. Take the face from image 1 and the bag from image 2, create a product photo"
              value={instructionField.value}
              onChange={instructionField.onChange}
              onFocus={instructionField.onFocus}
              onBlur={instructionField.onBlur}
              fullWidth
              className="nodrag nowheel"
            />

            <Button
              variant="contained"
              size="small"
              onClick={handleBlend}
              disabled={data.isGenerating || blendInputs.length < 1 || !data.instruction.trim()}
              startIcon={data.isGenerating ? <CircularProgress size={14} /> : <Blender sx={{ fontSize: 14 }} />}
              fullWidth
              color="warning"
            >
              {data.isGenerating ? 'Blending…' : 'Blend'}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              1st image = base, 2nd = overlay (logo/watermark)
            </Typography>

            <Box className="nodrag nowheel" sx={{ px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Overlay scale: {Math.round(overlayScale * 100)}%
              </Typography>
              <Slider
                size="small"
                min={0.05}
                max={0.8}
                step={0.05}
                value={overlayScale}
                onChange={(_, v) => updateNodeData(id, { overlayScale: v as number })}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
              />
            </Box>

            <Button
              variant="contained"
              size="small"
              onClick={handleOverlay}
              disabled={data.isGenerating || blendInputs.length < 2}
              startIcon={data.isGenerating ? <CircularProgress size={14} /> : <Layers sx={{ fontSize: 14 }} />}
              fullWidth
              color="warning"
            >
              {data.isGenerating ? 'Compositing…' : 'Composite'}
            </Button>
          </>
        )}

        {/* Result preview */}
        {data.resultImage && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1/1',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <img
              src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
              alt="Blended"
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
              onClick={() => setLightbox(true)}
            />
            <SaveToLibraryButton assetId={data.assetId} defaultName={data.instruction} />
          </Box>
        )}
        {data.resultImage && (
          <MediaLightbox
            open={lightbox}
            onClose={() => setLightbox(false)}
            src={getAssetSrc(data.resultImage, data.resultMimeType || 'image/png')}
            type="image"
          />
        )}

        {/* Generation history strip */}
        {data.generations && data.generations.length > 1 && (
          <GenerationStrip
            generations={data.generations}
            selectedIndex={data.selectedGeneration ?? data.generations.length - 1}
            onSelect={handleSelectGeneration}
            type="image"
          />
        )}
      </Box>

      {/* Output handle */}
      {data.resultImage && (
        <Handle
          type="source"
          position={Position.Right}
          id={HANDLES.imageOut}
          style={{ background: '#90caf9', width: 10, height: 10 }}
        />
      )}
    </Box>
  );
}

export default memo(BlendNode);
